import { MdEdit } from 'react-icons/md';
import type { AgentApiAllianceApplicationsItem } from '@/types/serializers';
import { Button } from '@/components/Button';
import UsersMenu from '@/features/agent/components/UsersMenu';
import type { ActiveUser } from '@/features/agent/types/ActiveUser';

interface ApplyListProps {
  application: AgentApiAllianceApplicationsItem;
  activeUsers: ActiveUser[];
  onStatusChange: (status: string) => void;
  onAssigneesChange: (companyId: number, userIds: number[]) => void;
  onMemoClick: () => void;
  onDetailClick: () => void;
}

const nextAction: Record<string, { label: string; status: string } | undefined> = {
  applied: { label: '次の選考に進む', status: 'document_screening' },
  document_screening: { label: '次の選考に進む', status: 'in_progress' },
  in_progress: { label: '内定', status: 'offered' },
  offered: { label: '承諾', status: 'accepted' },
};

const COMPLETED_STATUSES = ['accepted', 'candidate_declined', 'rejected'];

const formatDate = (dateString: string | null) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '-';
  return `${date.getFullYear()}/${date.getMonth() + 1}/${date.getDate()}`;
};

const formatMessageTime = (dateString: string | null) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const isToday =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();
  if (isToday) {
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  }
  return formatDate(dateString);
};

export default function ApplyList({
  application,
  activeUsers,
  onStatusChange,
  onAssigneesChange,
  onMemoClick,
  onDetailClick,
}: ApplyListProps) {
  const company = application.company;
  const companyAssigneeIds = company?.organization_users.map((user) => user.id) ?? [];
  const isCompleted = COMPLETED_STATUSES.includes(application.status);
  const primaryAction = nextAction[application.status];
  const isReceived = application.last_message_direction === 'received';
  const hasUnread = application.unread_message_count_for_organization_user > 0;
  const isUnreadMessage = isReceived && hasUnread;

  return (
    <div
      className="flex cursor-pointer items-center gap-lg rounded bg-white px-xl py-md shadow-layer1 transition-shadow hover:shadow-layer2"
      onClick={onDetailClick}
    >
      <div className="flex w-[179px] shrink-0 flex-col gap-xxs overflow-hidden whitespace-nowrap">
        <p className="text-body-xs text-sub">{formatDate(application.applied_at)}</p>
        <p className="truncate text-heading-xs font-bold">{application.candidate_name}</p>
        <p className="truncate text-body-xs">{application.agent_name}</p>
      </div>

      <div className="flex w-[348px] min-w-0 shrink flex-col items-start gap-xxs overflow-hidden pr-xl">
        <p className="w-full truncate text-heading-xxs font-bold">{company?.name ?? ''}</p>
        <p className="w-full truncate text-body-xs">{application.job_title}</p>
        {company && (
          <div onClick={(e) => e.stopPropagation()}>
            <UsersMenu
              activeUsers={activeUsers}
              selectedUserIds={companyAssigneeIds}
              onSelect={(userIds) => onAssigneesChange(company.id, userIds)}
              emptyLabel="未設定"
            />
          </div>
        )}
      </div>

      <div className="flex w-[111px] shrink-0 flex-col justify-center gap-xs">
        <p className="text-body-sm">{application.status_label}</p>
        <Button
          color="secondary"
          size="sm"
          iconOnly
          title="社内メモ"
          onClick={(e) => {
            e.stopPropagation();
            onMemoClick();
          }}
        >
          <MdEdit className="size-4" />
        </Button>
      </div>

      <div className="flex shrink-0 grow items-center gap-xs" onClick={(e) => e.stopPropagation()}>
        {!isCompleted && (
          <>
            {primaryAction && (
              <Button color="primary" onClick={() => onStatusChange(primaryAction.status)}>
                {primaryAction.label}
              </Button>
            )}
            <Button color="secondary" onClick={() => onStatusChange('rejected')}>
              不採用
            </Button>
            <Button color="secondary" onClick={() => onStatusChange('candidate_declined')}>
              候補者辞退
            </Button>
          </>
        )}
      </div>

      <div className="flex w-[116px] shrink-0 flex-col items-end justify-center gap-xxs">
        {application.last_message_at && (
          <>
            <div className="flex items-center gap-xxs">
              <p
                className={`text-heading-3xs font-bold ${isUnreadMessage ? 'text-primary' : 'text-sub'}`}
              >
                {isReceived ? '受信' : '送信'}
              </p>
              {isUnreadMessage && <span className="size-2 rounded-full bg-primary" />}
            </div>
            <p className={`text-body-xs ${isUnreadMessage ? 'text-primary' : 'text-sub'}`}>
              {formatMessageTime(application.last_message_at)}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
